import { XIcon } from "@heroicons/react/outline";
import { HttpError, useCreate, useOne, useTranslation } from "@refinedev/core";
import DateTimePicker from "components/datetimePicker";
import Input from "components/input";
import { Label } from "components/label";
import Select from "components/select";
import { endpointService } from "components/shared/services/endpointService";
import { SwitchButton } from "components/switchButton";
import {
  ApplyToLevel,
  ApplyToProductType,
  DiscountType,
  ICreateCouponForm,
  IPromotionConditionDetail,
  IPromotionEventIdsItem,
  ITicketByEventIdsItem,
  UserType,
} from "interfaces";
import moment from "moment";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import {
  APPLY_TO_LEVEL_OPTIONS,
  APPLY_TO_PRODUCT_TYPES,
  DISCOUNT_TYPES,
} from "utills/constants";
import { QuantityDiscountRules } from "../quantityDiscountRules";
import { get, isEmpty } from "lodash";
import { canAccessByType } from "utills/helpers";
import { twMerge } from "tailwind-merge";
import { Tag } from "components/tag";

export const ConditionSetting = ({
  eventCurrencies,
  setEventCurrencies,
  defaultConditionData,
  isFetchingEvents,
  eventIds,
  setDuplicateCodeModalConfig,
}: {
  eventCurrencies: string[];
  setEventCurrencies: Dispatch<SetStateAction<string[]>>;
  defaultConditionData: IPromotionConditionDetail;
  isFetchingEvents: boolean;
  eventIds: IPromotionEventIdsItem[];
  setDuplicateCodeModalConfig: Dispatch<any>;
}) => {
  const { translate } = useTranslation();
  const { mutateAsync: postMutationAsync, isLoading: isLoading } = useCreate();
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<ICreateCouponForm>(); // retrieve all hook methods
  const [hasInitialData, setHasInitialData] = useState(false);

  const applyToProductTypeWatch = useWatch({
    control: control,
    name: "apply_to_product_type",
  });
  const eventIdsWatch = useWatch({ control: control, name: "event_ids" });
  const discountTypeWatch = useWatch({
    control: control,
    name: "discount_type_display",
  });
  const campaignDiscountTypeWatch = useWatch({
    control: control,
    name: "discount_type",
  });
  const applyToLevelWatch = useWatch({
    control: control,
    name: "apply_to_level",
  });

  const quantityDiscountRuleArrController = useFieldArray({
    control: control,
    name: "quantity_discount_rules",
  });

  const { data: ticketData, isFetching: isFetchingTickets } = useOne<
    ITicketByEventIdsItem[],
    HttpError
  >({
    resource: endpointService.getPromotionEndPoint("getTicketsByEventIds", {
      eventIds: watch("event_ids").join(),
      productType:
        discountTypeWatch === DiscountType.GROUP_DISCOUNT
          ? "payment_group"
          : "ticket",
    }),
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!eventIdsWatch,
    } as any,
    successNotification: (data, values, resource) => {
      setAllTickets((data?.data ?? []) as ITicketByEventIdsItem[]);
      return {
        message: `Successfully fetched data from ${resource}.`,
        description: "Success with no errors",
        type: "success",
      };
    },
  });

  const tickets = (ticketData?.data ?? []).flatMap((item) => ({
    label: item.name_en,
    options: item.tickets.map((ticket) => ({
      value: ticket.id,
      label: ticket.name_en,
    })),
  }));

  const events = (eventIds ?? [])
    .filter((event) => {
      if (discountTypeWatch === DiscountType.QUANTITY_DISCOUNT) {
        return (
          event.id === defaultConditionData.event_id ||
          event.has_quantity_discount === false
        );
      }
      if (discountTypeWatch === DiscountType.CAMPAIGN_DISCOUNT) {
        return event.event_type === "sports";
      }
      return true;
    })
    .map((event: IPromotionEventIdsItem) => ({
      value: event.id,
      label: event.name_en,
      type: event.event_type,
      currency: event.currency,
      has_quantity_discount: event.has_quantity_discount,
    }));

  const discountTypesArr = DISCOUNT_TYPES.filter((type) => {
    const validTypes = [DiscountType.FIX_PRICE, DiscountType.PERCENT];
    if (
      discountTypeWatch === DiscountType.FIX_PRICE ||
      discountTypeWatch === DiscountType.PERCENT
    ) {
      return validTypes.includes(type.value);
    }
    return true;
  });

  const _checkConflicts = async (
    eventIds: string[],
    applyToProductType: ApplyToProductType
  ) => {
    try {
      const data = await postMutationAsync({
        resource: endpointService.getPromotionEndPoint("checkConflict", {
          conditionId: defaultConditionData?.id,
        }),
        invalidates: [],
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          event_ids: eventIds,
          apply_type: applyToProductType,
        },
      });
      return true;
    } catch (error) {
      const conflictCodes = get(error, "response.data.error.conflicts", []);
      setDuplicateCodeModalConfig({
        open: true,
        data: conflictCodes,
      });
      return false;
    }
  };

  useEffect(() => {
    if (!isEmpty(eventIds)) {
      getSelectedEventCurrencies();
    }
  }, [eventIds]);

  useEffect(() => {
    // Reset các trường liên quan khi thay đổi Apply For
    if (!hasInitialData) {
      setHasInitialData(true);
      return;
    }
    if (applyToProductTypeWatch === ApplyToProductType.ALL) {
      setValue("event_ids", []);
      setValue("apply_product_ids", []);
    } else if (
      applyToProductTypeWatch === ApplyToProductType.INCLUDE ||
      applyToProductTypeWatch === ApplyToProductType.EXCLUDE
    ) {
      setValue("event_ids", []);
      setValue("apply_product_ids", []);
    }
    clearDiscountTypeRelatedFields();
  }, [applyToProductTypeWatch, setValue]);

  useEffect(() => {
    if (!hasInitialData) {
      return;
    }
    if (eventIdsWatch) {
      setValue("apply_product_ids", []);
      getSelectedEventCurrencies();
    }
  }, [eventIdsWatch, setValue]);

  useEffect(() => {
    if (!hasInitialData) {
      return;
    }
    clearDiscountTypeRelatedFields();
    if (discountTypeWatch === DiscountType.PERCENT) {
      setValue("discount_value", 0);
    }
    setValue("max_times_used_per_account", null);
  }, [discountTypeWatch, setValue]);

  useEffect(() => {
    if (!hasInitialData) {
      return;
    }
    setValue("min_total", null);
    setValue("min_total_usd", null);
    if (applyToLevelWatch === ApplyToLevel.TOTAL_ORDER) {
      // Clear max applicable items, set default min order value
      setValue("apply_max_item_on_cart_qty", null);
      setAllTickets(ticketData?.data ?? []);
    } else if (applyToLevelWatch === ApplyToLevel.PER_ITEM) {
      // Clear min order value, set default max applicable items
      setValue("apply_max_item_on_cart_qty", 1);
    }
  }, [applyToLevelWatch, setValue]);

  const setAllTickets = (tickets: ITicketByEventIdsItem[]) => {
    if (
      applyToLevelWatch === ApplyToLevel.TOTAL_ORDER &&
      (discountTypeWatch === DiscountType.FIX_PRICE ||
        discountTypeWatch === DiscountType.PERCENT)
    ) {
      const allTickets = (tickets ?? []).flatMap((item) =>
        item.tickets
          .filter((ticket: any) => ticket.id)
          .map((ticket: any) => String(ticket.id))
      );
      setValue("apply_product_ids", allTickets ?? []);
    }
  };

  const clearDiscountTypeRelatedFields = () => {
    setValue("discount_value", null);
    setValue("discount_value_usd", null);

    setValue("max_discount_value", null);
    setValue("max_discount_value_usd", null);

    setValue("min_total", null);
    setValue("min_total_usd", null);

    setValue("apply_to_level", null);
    setValue("apply_max_item_on_cart_qty", null);
  };

  const clearCampaignDiscountTypeRelatedFields = () => {
    setValue("discount_value", null);
    setValue("discount_value_usd", null);

    setValue("max_discount_value", null);
    setValue("max_discount_value_usd", null);
  };

  const getSelectedEventCurrencies = () => {
    let selectedEventArr: any[] = [];
    if (applyToProductTypeWatch === ApplyToProductType.ALL) {
      selectedEventArr = events;
      // return !isEmpty(events.find((event) => event.currency === currency));
    }
    if (applyToProductTypeWatch === ApplyToProductType.EXCLUDE) {
      selectedEventArr = events.filter(
        (event) => !eventIdsWatch.includes(event.value)
      );
    }
    if (applyToProductTypeWatch === ApplyToProductType.INCLUDE) {
      selectedEventArr = events.filter((event) =>
        eventIdsWatch.includes(event.value)
      );
    }
    const uniqueCurrencies = (selectedEventArr ?? [])
      .map((obj) => obj.currency)
      .filter(
        (currency, index, array) =>
          typeof currency === "string" && array.indexOf(currency) === index
      );
    setEventCurrencies(uniqueCurrencies);
  };
  const shouldShowEventName = () => {
    const applyToProductTypesWithEventName = [
      ApplyToProductType.INCLUDE,
      ApplyToProductType.EXCLUDE,
    ];

    return applyToProductTypesWithEventName.includes(applyToProductTypeWatch);
  };

  const shouldShowApplyToLevel = () => {
    const discountTypesWithApplyToLevel = [
      DiscountType.PERCENT,
      DiscountType.FIX_PRICE,
      DiscountType.CAMPAIGN_DISCOUNT,
    ];
    return discountTypesWithApplyToLevel.includes(discountTypeWatch);
  };

  const shouldShowPercentDiscountValue = (
    discountType: DiscountType | null
  ) => {
    const discountTypeForDiscountValue: any[] = [DiscountType.PERCENT];
    return discountTypeForDiscountValue.includes(discountType);
  };
  const shouldShowFixedAmountDiscountValue = (
    discountType: DiscountType | null
  ) => {
    const discountTypeForDiscountValue: any[] = [DiscountType.FIX_PRICE];
    return discountTypeForDiscountValue.includes(discountType);
  };

  const shouldShowContentByEventCurrency = (currency: string) => {
    return eventCurrencies.includes(currency);
  };

  const shouldShowMinOrderValue = () => {
    return applyToLevelWatch === ApplyToLevel.TOTAL_ORDER;
  };

  const shouldShowMaxApplicableItems = () => {
    return false;
    // return applyToLevelWatch === ApplyToLevel.PER_ITEM;
  };

  const shouldShowTickets = () => {
    const applyToProductTypesWithTickets = [
      ApplyToProductType.INCLUDE,
      ApplyToProductType.EXCLUDE,
    ];

    return applyToProductTypesWithTickets.includes(applyToProductTypeWatch);
  };

  const shouldShowQuantityDiscountRules = () => {
    const discountTypeForQuantityDiscountRules = [
      DiscountType.GROUP_DISCOUNT,
      DiscountType.QUANTITY_DISCOUNT,
    ];

    return discountTypeForQuantityDiscountRules.includes(discountTypeWatch);
  };

  const shouldShowCampaignDiscount = () => {
    const discountTypeForCampaignDiscount = [DiscountType.CAMPAIGN_DISCOUNT];

    return discountTypeForCampaignDiscount.includes(discountTypeWatch);
  };

  const getAllOptionsFromGrouped = (groupedOptions: any[]) => {
    return groupedOptions.reduce((allOptions, group) => {
      return [...allOptions, ...group.options];
    }, []);
  };

  const _renderDiscountFields = (discountType: DiscountType | null) => {
    return (
      <>
        {shouldShowPercentDiscountValue(discountType) && (
          <>
            <div className="col-span-full sm:col-span-2">
              <Label required id="discount_value" name="Discount Value" />
              <div className="relative">
                <Input
                  id="discount_value"
                  type="number"
                  error={errors["discount_value"]}
                  placeholder="Discount Value"
                  {...register("discount_value")}
                  min={0}
                />
                <span className="w-3 bg-white absolute top-1/2 right-3 -translate-y-1/2">
                  %
                </span>
              </div>
              {errors["discount_value"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors?.discount_value.message}
                  </span>
                </p>
              ) : null}
            </div>
            {shouldShowContentByEventCurrency("vnd") ? (
              <div className="col-span-full sm:col-span-1">
                <Label id="max_discount_value" name="Max discount amount (đ)" />
                <div className="relative">
                  <Input
                    id="max_discount_value"
                    type="number"
                    error={errors["max_discount_value"]}
                    placeholder="Max discount amount"
                    {...register("max_discount_value")}
                    min={0}
                  />
                  <span className="w-3 bg-white absolute top-1/2 right-3 -translate-y-1/2">
                    đ
                  </span>
                </div>
                {errors["max_discount_value"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.max_discount_value.message}
                    </span>
                  </p>
                ) : null}
              </div>
            ) : (
              <></>
            )}
            {shouldShowContentByEventCurrency("usd") && (
              <div className="col-span-full sm:col-span-1">
                <Label
                  id="max_discount_value_usd"
                  name="Max discount amount ($)"
                />
                <div className="relative">
                  <Input
                    id="max_discount_value_usd"
                    type="number"
                    error={errors["max_discount_value_usd"]}
                    placeholder="Max discount amount"
                    {...register("max_discount_value_usd")}
                  />
                  <span className="w-3 bg-white absolute top-1/2 right-3 -translate-y-1/2">
                    $
                  </span>
                </div>
                {errors["max_discount_value_usd"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.max_discount_value_usd.message}
                    </span>
                  </p>
                ) : null}
              </div>
            )}
          </>
        )}
        {shouldShowFixedAmountDiscountValue(discountType) && (
          <>
            {shouldShowContentByEventCurrency("vnd") && (
              <div className="col-span-4 sm:col-span-2">
                <Label required id="discount_value" name="Discount Value" />
                <div className="relative">
                  <Input
                    id="discount_value"
                    type="number"
                    error={errors["discount_value"]}
                    placeholder="Discount Value"
                    {...register("discount_value")}
                    min={0}
                  />
                  <span className="w-3 bg-white absolute top-1/2 right-3 -translate-y-1/2">
                    đ
                  </span>
                </div>
                {errors["discount_value"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.discount_value.message}
                    </span>
                  </p>
                ) : null}
              </div>
            )}
            {shouldShowContentByEventCurrency("usd") && (
              <div className="col-span-4 sm:col-span-2">
                <Label required id="discount_value_usd" name="Discount Value" />
                <div className="relative">
                  <Input
                    id="discount_value_usd"
                    type="number"
                    error={errors["discount_value_usd"]}
                    placeholder="Discount Value"
                    {...register("discount_value_usd")}
                    min={0}
                  />
                  <span className="w-3 bg-white absolute top-1/2 right-3 -translate-y-1/2">
                    $
                  </span>
                </div>
                {errors["discount_value_usd"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.discount_value_usd.message}
                    </span>
                  </p>
                ) : null}
              </div>
            )}
          </>
        )}
      </>
    );
  };

  const handleRemoveGroup = (
    groupLabel: string,
    currentValue: string[],
    onChange: (value: string[]) => void
  ) => {
    const groupToRemove = tickets.find((group) => group.label === groupLabel);
    if (!groupToRemove) return;

    const groupOptionIds = groupToRemove.options.map((option) => option.value);

    const newValue = currentValue.filter((id) => !groupOptionIds.includes(id));
    onChange(newValue);
  };

  const allTicketOptions = getAllOptionsFromGrouped(tickets);
  const isDisabledApplyToProductType =
    discountTypeWatch === DiscountType.QUANTITY_DISCOUNT ||
    discountTypeWatch === DiscountType.GROUP_DISCOUNT ||
    discountTypeWatch === DiscountType.CAMPAIGN_DISCOUNT;
  const isDisabledDiscountTypeWatch =
    discountTypeWatch === DiscountType.QUANTITY_DISCOUNT ||
    discountTypeWatch === DiscountType.GROUP_DISCOUNT ||
    discountTypeWatch === DiscountType.CAMPAIGN_DISCOUNT;
  const isMultiEvent =
    discountTypeWatch === DiscountType.FIX_PRICE ||
    discountTypeWatch === DiscountType.PERCENT;
  const isDisabledEvent = discountTypeWatch === DiscountType.QUANTITY_DISCOUNT;

  return (
    <div className="w-full box-container">
      <div className="grid gap-x-4 gap-y-4 grid-cols-8 text-sm">
        <div className="col-span-full flex items-center justify-between">
          <p className="font-bold text-base">Condition Setting</p>
          <Controller
            control={control}
            name="is_active"
            render={({ field: { onChange, value = true, ref } }) => (
              <SwitchButton checked={value} onChange={onChange} />
            )}
          />
        </div>
        {canAccessByType(UserType.ADMIN) && (
          <div
            className={
              applyToProductTypeWatch === ApplyToProductType.ALL
                ? "col-span-full"
                : "col-span-full sm:col-span-4"
            }
          >
            <Label
              required
              id="apply_to_product_type"
              name="Apply For"
              hint="To apply Group Discount, Quantity Discount or Campaign/Special Discount, select 'Include Item(s)' in this field"
            />
            <Controller
              control={control}
              name="apply_to_product_type"
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Select
                    key="apply_to_product_type"
                    inputRef={ref}
                    options={APPLY_TO_PRODUCT_TYPES}
                    id="apply_to_product_type"
                    placeholder="Apply For"
                    isDisabled={isDisabledApplyToProductType}
                    defaultValue={
                      APPLY_TO_PRODUCT_TYPES.find(
                        (selected: { label: string; value: any }) =>
                          selected.value === value
                      ) ?? ""
                    }
                    value={APPLY_TO_PRODUCT_TYPES.find(
                      (selected: { label: string; value: any }) =>
                        selected.value === value
                    )}
                    onChange={async (selected: {
                      label: string;
                      value: any;
                    }) => {
                      if (selected?.value === ApplyToProductType.ALL) {
                        const isValid = await _checkConflicts(
                          [],
                          selected?.value
                        );
                        if (isValid) {
                          onChange(selected?.value);
                        }
                      } else {
                        onChange(selected?.value);
                      }
                    }}
                    error={errors["apply_to_product_type"]}
                  />
                );
              }}
            />
            {errors["apply_to_product_type"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors?.apply_to_product_type.message}
                </span>
              </p>
            ) : null}
          </div>
        )}
        {shouldShowEventName() ? (
          <div
            className={twMerge(
              "col-span-full",
              canAccessByType(UserType.ADMIN) ? "sm:col-span-4" : ""
            )}
          >
            <Label
              id="event_ids"
              name="Event Name"
              hint="To apply Group Discount, Quantity Discount or Campaign/Special Discount, select only 1 event in this field"
            />
            <Controller
              control={control}
              name="event_ids"
              defaultValue={[]}
              render={({ field: { onChange, value = [], ref } }) => (
                <Select
                  isMulti={isMultiEvent}
                  key="event_ids"
                  inputRef={ref}
                  options={events}
                  id="event_ids"
                  placeholder="Select event name"
                  isDisabled={isDisabledEvent}
                  value={value.map((id: string) => {
                    return events.find((item) => item.value === id);
                  })}
                  {...(isMultiEvent
                    ? {
                        onChange: async (
                          selected: { label: string; value: string }[]
                        ) => {
                          if (isEmpty(selected)) {
                            onChange(selected.map((item) => `${item.value}`));
                          } else {
                            const isValid = await _checkConflicts(
                              selected.map((item) => `${item.value}`),
                              applyToProductTypeWatch
                            );
                            if (isValid) {
                              onChange(selected.map((item) => `${item.value}`));
                            }
                          }
                        },
                      }
                    : {
                        onChange: (selected: {
                          label: string;
                          value: string;
                        }) => {
                          onChange([selected?.value]);
                        },
                      })}
                  error={errors["event_ids"]}
                  isLoading={isFetchingEvents}
                />
              )}
            />
            {errors["event_ids"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors["event_ids"].message}
                </span>
              </p>
            ) : null}
          </div>
        ) : (
          <></>
        )}
        <div className="col-span-full sm:col-span-4">
          <Label required id="note" name="Condition Name" />
          <Input
            id="note"
            type="text"
            error={errors["note"]}
            placeholder="Condition Name"
            {...register("note")}
          />
          {errors["note"] ? (
            <p className="error-text">
              <span className="font-medium">{errors["note"].message}</span>
            </p>
          ) : null}
        </div>
        <div className="col-span-4 sm:col-span-2">
          <Label required id="apply_from" name="Apply From" />
          <Controller
            control={control}
            name="apply_from"
            render={({ field: { onChange, value = "" } }) => {
              return (
                <DateTimePicker
                  id="apply_from"
                  placeholder="Apply From"
                  error={errors["apply_from"]}
                  value={value}
                  onChange={(date: any) =>
                    date === null
                      ? onChange("")
                      : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))
                  }
                />
              );
            }}
          />
          {errors["apply_from"] ? (
            <p className="error-text">
              <span className="font-medium">
                {errors["apply_from"].message}
              </span>
            </p>
          ) : null}
        </div>
        <div className="col-span-4 sm:col-span-2">
          <Label required id="apply_to" name="Apply To" />
          <Controller
            control={control}
            name="apply_to"
            render={({ field: { onChange, value = "" } }) => {
              return (
                <DateTimePicker
                  id="apply_to"
                  placeholder="Apply To"
                  error={errors["apply_to"]}
                  value={value}
                  onChange={(date: any) =>
                    date === null
                      ? onChange("")
                      : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))
                  }
                />
              );
            }}
          />
          {errors["apply_to"] ? (
            <p className="error-text">
              <span className="font-medium">{errors["apply_to"].message}</span>
            </p>
          ) : null}
        </div>
        <div className="col-span-full sm:col-span-4">
          <Label required id="discount_type_display" name="Discount Type" />
          <Controller
            control={control}
            name="discount_type_display"
            render={({ field: { onChange, value = "", ref } }) => {
              return (
                <Select
                  key="discount_type_display"
                  inputRef={ref}
                  options={discountTypesArr}
                  id="discount_type_display"
                  placeholder="Select discount type"
                  isDisabled={isDisabledDiscountTypeWatch}
                  defaultValue={
                    discountTypesArr.find(
                      (selected: { label: string; value: any }) =>
                        selected.value === value
                    ) ?? ""
                  }
                  value={discountTypesArr.find(
                    (selected: { label: string; value: any }) =>
                      selected.value === value
                  )}
                  onChange={(selected: { label: string; value: any }) => {
                    onChange(selected?.value);
                  }}
                  error={errors["discount_type_display"]}
                />
              );
            }}
          />
          {errors["discount_type_display"] ? (
            <p className="error-text">
              <span className="font-medium">
                {errors?.discount_type_display.message}
              </span>
            </p>
          ) : null}
        </div>
        {_renderDiscountFields(discountTypeWatch)}
        {shouldShowApplyToLevel() ? (
          <div className="col-span-full sm:col-span-4">
            <Label required id="apply_to_level" name="Apply To Level" />
            <Controller
              control={control}
              name="apply_to_level"
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Select
                    key={`${watch("apply_to_level")}_apply_to_level`}
                    inputRef={ref}
                    options={APPLY_TO_LEVEL_OPTIONS}
                    id="apply_to_level"
                    placeholder="Apply To Level"
                    defaultValue={
                      APPLY_TO_LEVEL_OPTIONS.find(
                        (selected: { label: string; value: any }) =>
                          selected.value === value
                      ) ?? ""
                    }
                    value={APPLY_TO_LEVEL_OPTIONS.find(
                      (selected: { label: string; value: any }) =>
                        selected.value === value
                    )}
                    onChange={(selected: { label: string; value: any }) => {
                      onChange(selected?.value);
                    }}
                    error={errors["apply_to_level"]}
                  />
                );
              }}
            />
            {errors["apply_to_level"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors?.apply_to_level.message}
                </span>
              </p>
            ) : null}
          </div>
        ) : (
          <></>
        )}
        {shouldShowMinOrderValue() ? (
          <>
            {shouldShowContentByEventCurrency("vnd") ? (
              <div className="col-span-full sm:col-span-2">
                <Label id="min_total" name="Min order value (đ)" />
                <div className="relative">
                  <Input
                    id="min_total"
                    type="number"
                    error={errors["min_total"]}
                    placeholder="Min order value"
                    {...register("min_total")}
                    min={0}
                  />
                  <span className="w-3 bg-white absolute top-1/2 right-3 -translate-y-1/2">
                    đ
                  </span>
                </div>
                {errors["min_total"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.min_total.message}
                    </span>
                  </p>
                ) : null}
              </div>
            ) : (
              <></>
            )}
            {shouldShowContentByEventCurrency("usd") ? (
              <div className="col-span-full sm:col-span-2">
                <Label id="min_total_usd" name="Min order value ($)" />
                <div className="relative">
                  <Input
                    id="min_total_usd"
                    type="number"
                    error={errors["min_total_usd"]}
                    placeholder="Min order value"
                    {...register("min_total_usd")}
                    min={0}
                  />
                  <span className="w-3 bg-white absolute top-1/2 right-3 -translate-y-1/2">
                    $
                  </span>
                </div>
                {errors["min_total_usd"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.min_total_usd.message}
                    </span>
                  </p>
                ) : null}
              </div>
            ) : (
              <></>
            )}
          </>
        ) : (
          <></>
        )}
        {shouldShowMaxApplicableItems() ? (
          <div className="col-span-full sm:col-span-4">
            <Label
              required
              id="apply_max_item_on_cart_qty"
              name="Max Applicable Items"
            />
            <Input
              id="apply_max_item_on_cart_qty"
              type="number"
              error={errors["apply_max_item_on_cart_qty"]}
              placeholder="Max Applicable Items"
              {...register("apply_max_item_on_cart_qty")}
              min={1}
            />
            {errors["apply_max_item_on_cart_qty"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors?.apply_max_item_on_cart_qty.message}
                </span>
              </p>
            ) : null}
          </div>
        ) : (
          <></>
        )}
        {shouldShowTickets() ? (
          <>
            {applyToLevelWatch === ApplyToLevel.TOTAL_ORDER &&
            (discountTypeWatch === DiscountType.FIX_PRICE ||
              discountTypeWatch === DiscountType.PERCENT) ? (
              !isEmpty(watch("event_ids") ?? []) ? (
                <div className="col-span-full">
                  <div>
                    <Label id="apply_product_ids" name="Tickets" required />
                    <div className="flex flex-col gap-y-4">
                      {(tickets ?? []).map((ticket, index) => (
                        <div key={index}>
                          <p>{ticket.label}</p>
                          <div className="flex items-center flex-wrap gap-2.5 mt-2">
                            {isEmpty(ticket.options ?? []) && (
                              <p className="text-xs text-gray-500">
                                {translate("no-data")}
                              </p>
                            )}
                            {(ticket.options ?? []).map(
                              (ticketOption) =>
                                watch("apply_product_ids").includes(
                                  ticketOption.value
                                ) && (
                                  <Tag
                                    text={ticketOption.label ?? ""}
                                    key={ticketOption.value ?? ""}
                                  />
                                )
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {errors["apply_product_ids"] ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {errors["apply_product_ids"]?.message}
                        </span>
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : (
                <></>
              )
            ) : (
              <div className="col-span-full">
                <div>
                  <Label id="apply_product_ids" name="Tickets" required />
                  <Controller
                    control={control}
                    name="apply_product_ids"
                    defaultValue={[]}
                    render={({ field: { onChange, value = [], ref } }) => (
                      <Select
                        isMulti
                        key="apply_product_ids"
                        inputRef={ref}
                        options={tickets}
                        id="apply_product_ids"
                        placeholder="Select tickets"
                        value={allTicketOptions.filter((option: any) =>
                          value.includes(option.value)
                        )}
                        onChange={(selectedOptions: any) => {
                          const selectedIds = selectedOptions
                            ? selectedOptions.map((option: any) => option.value)
                            : [];
                          onChange(selectedIds);
                        }}
                        error={errors["apply_product_ids"]}
                        isLoading={isFetchingTickets}
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        isClearable={true}
                        formatGroupLabel={(data: any) => {
                          const groupOptions = data.options || [];
                          const groupOptionIds = groupOptions.map(
                            (option: any) => option.value
                          );
                          const hasSelectedTickets = groupOptionIds.some(
                            (id: any) => value.includes(id)
                          );

                          return (
                            <div className="flex items-center gap-x-2">
                              <span>{data.label}</span>
                              {hasSelectedTickets && (
                                <XIcon
                                  className="size-3 cursor-pointer"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRemoveGroup(
                                      data.label,
                                      value,
                                      onChange
                                    );
                                  }}
                                />
                              )}
                            </div>
                          );
                        }}
                      />
                    )}
                  />
                  {errors["apply_product_ids"] ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {errors["apply_product_ids"]?.message}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>
            )}
          </>
        ) : (
          <></>
        )}
        {shouldShowQuantityDiscountRules() && (
          <div className="col-span-full">
            <QuantityDiscountRules
              quantityDiscountRuleArrController={
                quantityDiscountRuleArrController
              }
              shouldShowContentByEventCurrency={
                shouldShowContentByEventCurrency
              }
              discountTypeWatch={discountTypeWatch}
            />
          </div>
        )}
        {shouldShowCampaignDiscount() && (
          <>
            <div className="col-span-full sm:col-span-2">
              <Label required id="campaign_code" name="Campaign Code" />
              <Input
                id="campaign_code"
                type="text"
                error={errors["campaign_code"]}
                placeholder="Campaign Code"
                maxLength={32}
                {...register("campaign_code")}
              />
              {errors["campaign_code"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["campaign_code"].message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="col-span-full sm:col-span-2">
              <Label required id="discount_type" name="Discount Type" />
              <Controller
                control={control}
                name="discount_type"
                render={({ field: { onChange, value = "", ref } }) => {
                  return (
                    <Select
                      key="discount_type"
                      inputRef={ref}
                      options={discountTypesArr.filter(
                        (type) =>
                          type.value === DiscountType.FIX_PRICE ||
                          type.value === DiscountType.PERCENT
                      )}
                      id="discount_type"
                      placeholder="Select discount type"
                      defaultValue={
                        discountTypesArr.find(
                          (selected: { label: string; value: any }) =>
                            selected.value === value
                        ) ?? ""
                      }
                      value={discountTypesArr.find(
                        (selected: { label: string; value: any }) =>
                          selected.value === value
                      )}
                      onChange={(selected: { label: string; value: any }) => {
                        onChange(selected?.value);
                        clearCampaignDiscountTypeRelatedFields();
                      }}
                      error={errors["discount_type"]}
                    />
                  );
                }}
              />
              {errors["discount_type"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["discount_type"]?.message}
                  </span>
                </p>
              ) : null}
            </div>

            {_renderDiscountFields(campaignDiscountTypeWatch)}
          </>
        )}
      </div>
    </div>
  );
};
